import React, { useState } from 'react';
import { logger } from '@/utils/logger';
import { useInventoryItems, useCreateInventoryItem, useDeleteInventoryItem } from '@/hooks/usePSI';
import { IInventoryItem, IInventoryCreateRequest } from '@/types/psi';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';
import InventoryOperationSelector from './InventoryOperationSelector';
import { MobileCard } from '@/components/ui';

const InventoryList: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showOperations, setShowOperations] = useState(true);
  const [newItem, setNewItem] = useState<Partial<IInventoryCreateRequest>>({
    currency: 'TWD',
  });

  const { data, isLoading, refetch } = useInventoryItems({ page, pageSize, q: searchQuery });
  const createMutation = useCreateInventoryItem();
  const deleteMutation = useDeleteInventoryItem();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleAddNew = async () => {
    if (!newItem.part_code) {
      alert('料號 is required');
      return;
    }
    if (!newItem.warehouse) {
      alert('倉庫別 is required');
      return;
    }

    try {
      await createMutation.mutateAsync(newItem as IInventoryCreateRequest);
      setIsAddingNew(false);
      setNewItem({ currency: 'TWD' });
    } catch (error) {
      logger.error('Failed to create inventory item:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('確定要刪除此庫存項目嗎？')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        logger.error('Failed to delete inventory item:', error);
      }
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Operations Wheel Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowOperations(!showOperations)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span className="font-medium text-gray-900 dark:text-white">庫存作業</span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${showOperations ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showOperations && (
          <div className="px-4 pb-4">
            <InventoryOperationSelector />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 sm:justify-between sm:items-center">
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="搜尋庫存..."
        />
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => refetch()}
            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] active:scale-[0.98] transition-all"
          >
            重新整理
          </button>
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 min-h-[44px] active:scale-[0.98] transition-all"
          >
            <span className="hidden xs:inline">+ </span>新增<span className="hidden xs:inline">項目</span>
          </button>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {data?.items?.map((item: IInventoryItem) => (
          <MobileCard
            key={item.id}
            title={item.part_code || '-'}
            subtitle={`${item.specification || '-'} | ${item.warehouse || '-'}`}
            status={
              item.movement_type && (
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  item.movement_type === '進貨' ? 'bg-green-100 text-green-800' :
                  item.movement_type === '出貨' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {item.movement_type}
                </span>
              )
            }
            fields={[
              { label: '日期', value: item.date || '-' },
              { label: '數量', value: item.quantity ?? '-' },
              { label: '單價', value: item.unit_price != null ? `${item.unit_price.toFixed(2)} ${item.currency || 'TWD'}` : '-' },
              { label: '單號', value: item.document_number || '-' },
            ]}
            actions={[
              { label: '刪除', onClick: () => handleDelete(item.id), variant: 'danger' },
            ]}
          />
        ))}
        {(!data?.items || data.items.length === 0) && !isAddingNew && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            目前沒有庫存項目
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">月份</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">異動類型</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">主類別</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">子類別</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">日期</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">發票號碼</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">發票日期</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">倉庫別</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">單別</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">單號</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">料號</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">規格</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">數量</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">單價</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">幣別</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {isAddingNew && (
              <tr className="bg-green-50">
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="月份"
                    value={newItem.month || ''}
                    onChange={(e) => setNewItem({ ...newItem, month: e.target.value })}
                    className="w-16 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={newItem.movement_type || ''}
                    onChange={(e) => setNewItem({ ...newItem, movement_type: e.target.value })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  >
                    <option value="">選擇...</option>
                    <option value="進貨">進貨</option>
                    <option value="出貨">出貨</option>
                    <option value="盤點">盤點</option>
                    <option value="調整">調整</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="主類別"
                    value={newItem.main_category || ''}
                    onChange={(e) => setNewItem({ ...newItem, main_category: e.target.value })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="子類別"
                    value={newItem.sub_category || ''}
                    onChange={(e) => setNewItem({ ...newItem, sub_category: e.target.value })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="date"
                    value={newItem.date || ''}
                    onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                    className="w-32 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="發票號碼"
                    value={newItem.invoice_number || ''}
                    onChange={(e) => setNewItem({ ...newItem, invoice_number: e.target.value })}
                    className="w-24 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="date"
                    value={newItem.invoice_date || ''}
                    onChange={(e) => setNewItem({ ...newItem, invoice_date: e.target.value })}
                    className="w-32 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="倉庫別"
                    value={newItem.warehouse || ''}
                    onChange={(e) => setNewItem({ ...newItem, warehouse: e.target.value })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={newItem.document_type || ''}
                    onChange={(e) => setNewItem({ ...newItem, document_type: e.target.value })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  >
                    <option value="">選擇...</option>
                    <option value="採購單">採購單</option>
                    <option value="銷售單">銷售單</option>
                    <option value="盤點單">盤點單</option>
                    <option value="調整單">調整單</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="單號"
                    value={newItem.document_number || ''}
                    onChange={(e) => setNewItem({ ...newItem, document_number: e.target.value })}
                    className="w-24 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="料號"
                    value={newItem.part_code || ''}
                    onChange={(e) => setNewItem({ ...newItem, part_code: e.target.value })}
                    className="w-24 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    placeholder="規格"
                    value={newItem.specification || ''}
                    onChange={(e) => setNewItem({ ...newItem, specification: e.target.value })}
                    className="w-28 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    placeholder="數量"
                    value={newItem.quantity || ''}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })}
                    className="w-16 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="單價"
                    value={newItem.unit_price || ''}
                    onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 text-sm border rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={newItem.currency || 'TWD'}
                    onChange={(e) => setNewItem({ ...newItem, currency: e.target.value })}
                    className="w-16 px-2 py-1 text-sm border rounded"
                  >
                    <option value="TWD">TWD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="JPY">JPY</option>
                    <option value="CNY">CNY</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={handleAddNew}
                    disabled={createMutation.isLoading}
                    className="text-green-600 hover:text-green-800 mr-2"
                  >
                    儲存
                  </button>
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
                  >
                    取消
                  </button>
                </td>
              </tr>
            )}
            {data?.items?.map((item: IInventoryItem) => (
              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">{item.month || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">{item.movement_type || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.main_category || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.sub_category || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">{item.date || '-'}</td>
                <td className="px-3 py-3 text-sm font-mono text-gray-900 dark:text-white">{item.invoice_number || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.invoice_date || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">{item.warehouse || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.document_type || '-'}</td>
                <td className="px-3 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">{item.document_number || '-'}</td>
                <td className="px-3 py-3 text-sm font-medium text-indigo-600">{item.part_code || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.specification || '-'}</td>
                <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.quantity ?? '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">{item.unit_price != null ? item.unit_price.toFixed(2) : '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.currency || '-'}</td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deleteMutation.isLoading}
                    className="text-red-600 hover:text-red-800"
                  >
                    刪除
                  </button>
                </td>
              </tr>
            ))}
            {(!data?.items || data.items.length === 0) && !isAddingNew && (
              <tr>
                <td colSpan={16} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  目前沒有庫存項目。點擊「+ 新增項目」來建立。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={data.total_pages}
          totalItems={data.total_count || 0}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      {/* Summary */}
      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
        顯示 {data?.items?.length || 0} / {data?.total_count || 0} 筆資料
      </div>
    </div>
  );
};

export default InventoryList;
